// districtModel — THE definition of "which districts exist, how they nest, and
// what one is called". Every surface that writes or reads a district code goes
// through here.
// ============================================================================
// WHY THIS EXISTS (owner, 2026-08-08: "make one canonical form … so everything
// writes and reads similarly")
//
// There were FOUR district pickers with four structures and three naming rules:
//
//   search sidebar   tree, city-scoped, parent→child, counts, gray-out
//   add property     <select> + optgroups; city supported but never passed
//   add client       flat list grouped by d.city, ignoring parent_code entirely
//   fill form        raw flat <select> — no groups, no city, no search
//
// So the same district rendered under three different names, in three different
// orders, in a list that in two places held all 236 rows of Georgia with the
// Adjara/Batumi block sorting first — which is how a Tbilisi address ends up
// showing "აეროპორტი" (Batumi) as its district.
//
// This module is the ONE model; the pickers are renderings of it. Same rule as
// district_keys_v1 (B273) and property_cluster(): one definition, many readers,
// and nothing re-derives it locally.
//
// ⚠️ DEPENDENCY-FREE BY CONTRACT. The public fill form (/fill.html) imports this,
// and that bundle must stay free of authed code — same rule as lib/phoneRules and
// lib/householdVocab. Import nothing here but other pure modules. In particular:
// no db/, no i18n, no components. Labels are chosen by an explicit `lang`
// argument, never by reading global language state.
// ============================================================================

import { georgianStem } from '../../core/georgian-stem.mjs'

// Tbilisi is ~everything: it holds 64 of the 236 rows and the overwhelming
// majority of supply. A capture form that opens on the whole country makes the
// common case the hardest one.
export const DEFAULT_CITY = 'Tbilisi'

// ── ONE naming rule ─────────────────────────────────────────────────────────
// Was three: `prefer==='ka' ? name_ka||name_en : name_en||name_ka` in the
// property modal, always `name_ka||name_en` in the client modal, and the fill
// form's own `dName`. Prefer the reader's language, fall back to whatever exists,
// and show the raw code only when the row is nameless — never blank, because a
// blank option is indistinguishable from a missing one.
export function districtLabel(d, lang = 'ka') {
  if (!d) return ''
  const order = lang === 'ka' ? [d.name_ka, d.name_en, d.name_ru]
    : lang === 'ru' ? [d.name_ru, d.name_en, d.name_ka]
    : [d.name_en, d.name_ka, d.name_ru]
  return order.find(Boolean) || d.code
}

// ── city membership ─────────────────────────────────────────────────────────
// MOVED here verbatim from lib/deskFeed.js (which now re-exports these two, so
// the search side keeps working and there is still only one implementation).
// ⚠️ MEMOISED, and that is not a micro-optimisation — it was 64% of a Desk click.
// Profiled 2026-08-09 (B618): `cityKey` is called from inside `districtInCity`'s inner
// scan, which is itself called per district, from loops that are themselves per city.
// One measured path — IntakeTab's per-city district-count map — came to
// 69 cities × 236 districts × 236 inner rows ≈ 3.8 M iterations, each doing 2–3 of these
// regex + Georgian-stem passes. DevTools attributed 4,308 ms of self time (64.3% of the
// whole click) to this function's bundle frame.
// The input domain is tiny and fixed (district and city names — a few hundred distinct
// strings), so a plain Map is bounded by the data, not by traffic.
const CITY_KEY_MEMO = new Map()
export function cityKey(s) {
  if (!s) return ''
  const hit = CITY_KEY_MEMO.get(s)
  if (hit !== undefined) return hit
  const bare = String(s).trim().toLowerCase().replace(/\s*(მუნიციპალიტეტი|municipality)\s*$/u, '').trim()
  const stem = georgianStem(bare)
  // Georgian syncope: the genitive drops a stem vowel (გარდაბანი→გარდაბნის), which the
  // stemmer can't undo generically. Closed set of city names where it occurs.
  const SYNCOPE = { 'გარდაბნ': 'გარდაბან', 'ზესტაფნ': 'ზესტაფონ', 'წყალტუბ': 'წყალტუბო' }
  const out = SYNCOPE[stem] || stem
  CITY_KEY_MEMO.set(s, out)
  return out
}

// City-level rows (MUN_*/TWN_*) indexed by their cityKey, built ONCE per districts array.
// This replaces the `all.find(...)` that `districtInCity` ran on every call — the O(n²)
// half of the same defect. Keyed by array IDENTITY in a WeakMap: the districts list comes
// from `useState` and keeps its identity between renders, so the index is built once per
// load and collected with the array. A new array (a refetch) simply builds a new index.
const CITY_ROW_INDEX = new WeakMap()
function cityRowIndex(all) {
  let idx = CITY_ROW_INDEX.get(all)
  if (idx) return idx
  idx = new Map()
  for (const r of all) {
    if (!/^(MUN_|TWN_)/.test(r.code || '')) continue
    // First writer wins, preserving `.find`'s first-match semantics exactly.
    for (const x of [r.name_ka, r.name_en]) {
      const k = x && cityKey(x)
      if (k && !idx.has(k)) idx.set(k, r)
    }
  }
  CITY_ROW_INDEX.set(all, idx)
  return idx
}

export function districtInCity(d, city, all = []) {
  if (!city || city === 'all') return true
  const c = city.trim().toLowerCase()
  // ⚠️ Tbilisi membership is by CODE PREFIX, not the `city` column. Migration 0061 stamped
  // city='Tbilisi' as a DEFAULT across the whole Georgia hierarchy (REG_*, MUN_*, and the
  // RUS_/BAT_/KUT_/TWN_ other-city neighborhoods all carry city='Tbilisi'), so a city-column
  // test dumped Adjara/Imereti/Batumi/etc. into the Tbilisi district dropdown. The genuinely-
  // Tbilisi rows are TBS_* (raions + zones) and PERI_* (the suburb belt sold as Tbilisi).
  if (c === 'თბილისი' || c === 'tbilisi') return /^(TBS_|PERI_)/.test(d.code)
  // non-Tbilisi: the row IS the city (keyed name comparison, so "მცხეთა" also matches the
  // "მცხეთის მუნიციპალიტეტი" row)…
  const key = cityKey(c)
  if (d.city !== 'Tbilisi' && [d.name_ka, d.name_en, d.city].some(x => x && cityKey(x) === key)) return true
  // …or the row belongs to that city via the `city` column (reliable since the 0067 backfill,
  // 2026-07-07 — Batumi/Kutaisi/Rustavi neighborhoods carry their real city now). The dropdown
  // label is Georgian while `city` is English, so resolve the label to its city-level row
  // (MUN_*/TWN_) first and compare English-to-English. Needs `all` — callers pass the full
  // districts list; without it this falls back to the name-only match above.
  // Was `all.find(...)` — a full scan on EVERY call, and this function is called per
  // district from loops that are themselves per city. Same answer, from an index built
  // once per districts array (see cityRowIndex).
  const cityRow = cityRowIndex(all).get(key)
  return !!cityRow && cityRow.city !== 'Tbilisi' && d.city === cityRow.city
}

// ⚠️ TWO CITY VOCABULARIES, and they are not the same language — the same seam
// core_street.js documents on the server, which also has to be crossed here. The
// city SELECTOR speaks `district.city`, which is ENGLISH ("Tbilisi", "Kutaisi"),
// while a street row's `city` is GEORGIAN ("თბილისი", "ქუთაისი"). Feeding one to
// the other silently produced a `<select>` whose value matched no option, so
// picking a Tbilisi street flipped the visible scope to "All of Georgia".
// Resolved through the district table — data we maintain anyway — not a literal
// list here that would rot. Tbilisi is special-cased for the reason
// `districtInCity` already records: the capital HAS no municipality row.
// An unknown name returns null, i.e. "leave the scope alone", never a guess.
export function cityValue(name, districts = []) {
  const n = String(name || '').trim()
  if (!n) return null
  if (/^(tbilisi|თბილის)/i.test(n)) return 'Tbilisi'
  const key = cityKey(n)
  const row = cityRowIndex(districts).get(key)   // was the same full scan as districtInCity
  return row?.city || null
}

// ── the hierarchy ───────────────────────────────────────────────────────────
// Lifted from DistrictFacetList's memo, including its cycle guard, and shared so
// a capture form nests exactly the way search nests.
//
// ⚠️ `district_boundary` is a six-level gazetteer built from imported OSM data and
// NOTHING validates parent_code on the way in, so a cycle — or a row that is its
// own parent — would make a recursive render loop forever. Each node's ancestry is
// walked ONCE here; a node whose chain revisits itself is treated as a root:
// visible and pickable, just not nested. Dropping a district from the list is a
// worse failure than dropping its indentation.
//
// `sort` defaults to label order. The search sidebar passes its own count-first
// comparator — the shape is shared, the ordering is the caller's business.
export function buildDistrictTree(districts, city = null, { lang = 'ka', sort = null } = {}) {
  const inCity = districts.filter(d => districtInCity(d, city, districts))
  const byCode = new Map(inCity.map(d => [d.code, d]))
  const cmp = sort || ((a, b) => districtLabel(a, lang).localeCompare(districtLabel(b, lang)))
  const childrenOf = new Map()
  // A hierarchy that has not loaded renders every row as a root, which looks
  // identical to the flat list this module exists to replace — so say so out loud
  // rather than silently degrading.
  const hierarchical = inCity.some(d => d.parent_code)
  if (!hierarchical) return { roots: inCity.slice().sort(cmp), childrenOf, byCode, hierarchical, inCity }
  const roots = []
  for (const d of inCity) {
    let p = d.parent_code, seen = new Set([d.code]), ok = true
    while (p) {
      if (seen.has(p) || !byCode.has(p)) { ok = false; break }
      seen.add(p); p = byCode.get(p).parent_code
    }
    if (d.parent_code && ok) {
      if (!childrenOf.has(d.parent_code)) childrenOf.set(d.parent_code, [])
      childrenOf.get(d.parent_code).push(d)
    } else roots.push(d)
  }
  for (const list of childrenOf.values()) list.sort(cmp)
  return { roots: roots.sort(cmp), childrenOf, byCode, hierarchical, inCity }
}

// The chain of names above a district — "საბურთალო › ვედზისი". A search result
// stripped of its parent is ambiguous in a country with three Rustavelis and two
// Vakisubanis; the path is what makes a hit selectable with confidence.
export function districtPath(d, byCode, lang = 'ka') {
  const names = []
  let cur = d, guard = 0
  while (cur && guard++ < 8) {
    names.unshift(districtLabel(cur, lang))
    cur = cur.parent_code ? byCode.get(cur.parent_code) : null
  }
  return names
}

// ── type-to-find ────────────────────────────────────────────────────────────
// Matches on every name we hold plus the raw code, in a Georgian-aware way: the
// stem is compared too, so "საბურთალოს" finds "საბურთალო". Latin and Cyrillic
// queries work against the same row because all three names are in the haystack.
export function districtSearchText(d) {
  return [d.name_ka, d.name_en, d.name_ru, d.code].filter(Boolean).join(' ').toLowerCase()
}

export function matchesDistrictQuery(d, query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return true
  const hay = districtSearchText(d)
  if (hay.includes(q)) return true
  const stem = georgianStem(q)
  return stem.length > 1 && hay.includes(stem)
}

// Flat, ranked hits for the search box: a prefix match on the label outranks a
// match anywhere else, so typing "vak" puts Vake above "Vakisubani in Kutaisi".
export function searchDistricts(districts, query, { city = null, lang = 'ka', limit = 40 } = {}) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return []
  const pool = districts.filter(d => districtInCity(d, city, districts))
  const scored = []
  for (const d of pool) {
    if (!matchesDistrictQuery(d, q)) continue
    const label = districtLabel(d, lang).toLowerCase()
    scored.push({ d, rank: label.startsWith(q) ? 0 : label.includes(q) ? 1 : 2 })
  }
  scored.sort((a, b) => a.rank - b.rank ||
    districtLabel(a.d, lang).localeCompare(districtLabel(b.d, lang)))
  return scored.slice(0, limit).map(x => x.d)
}

// ── cities ──────────────────────────────────────────────────────────────────
// The city selector's options. Tbilisi is first because it is the market; the
// rest follow by name. "All of Georgia" is the caller's own entry — this returns
// real places only, so nobody has to guess which sentinel string means "any".
export function citiesOf(districts, lang = 'ka') {
  const seen = new Map()
  for (const d of districts) {
    if (!/^(MUN_|TWN_)/.test(d.code)) continue
    const name = districtLabel(d, lang)
    // ⚠️ Dedupe on the STEM, not the label. The gazetteer holds the same place
    // twice under near-identical names — "Mtskheta" / "Mtskheta municipality",
    // "Sighnaghi" / "Signagi Municipality", "Gardabani municipality" /
    // "Gardabani Municipality" — and a label-keyed map keeps both, so the city
    // list renders visible duplicates that pick different rows. cityKey strips
    // the municipality suffix and stems, which is what it was written for.
    // Shortest label wins: "Mtskheta" over "Mtskheta municipality".
    //
    // ⚠️ Key on the GEORGIAN name whatever the display language. cityKey is a
    // Georgian stemmer, and the duplicates are only reliably identical there:
    // MUN_SIGHNAGHI vs MUN_SIGNAGI are "Sighnaghi" and "Signagi Municipality" in
    // English — different strings by transliteration, which no stemmer can join —
    // but სიღნაღი and სიღნაღის მუნიციპალიტეტი in Georgian, which it collapses.
    // Same for MUN_DEDOPLISTSKARO / MUN_DEDOPLISTSQARO. The label stays in the
    // reader's language; only the identity key is fixed to one.
    const key = cityKey(d.name_ka || name)
    const prev = seen.get(key)
    if (!prev || name.length < prev.name.length) seen.set(key, { code: d.code, name, city: d.city })
  }
  const rest = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  return [{ code: 'TBILISI', name: lang === 'ka' ? 'თბილისი' : lang === 'ru' ? 'Тбилиси' : 'Tbilisi', city: 'Tbilisi' },
    ...rest.filter(c => c.city !== 'Tbilisi' || !/^(TBS_|PERI_)/.test(c.code))]
}
